<script lang="ts">
    // Config
    import { ShelfConfig } from '@amp/web-app-components/config/components/shelf';
    import { ArtworkConfig } from '@amp/web-app-components/config/components/artwork';
    import { getShelfConfig } from '~/config/components/shelf';

    // Types
    import type { SvelteComponent } from 'svelte';
    import type { Section } from '@jet-app/music-classical/models/section/Section';

    // Stores
    import { listScreenStore } from '~/stores/list-screen';
    import { sidebarIsHidden } from '@amp/web-app-components/src/stores/sidebar-hidden';

    // Utils
    import { getMediaConditions } from '@amp/web-app-components/src/utils/getMediaConditions';
    import { buildMediaQueryStore } from '@amp/web-app-components/src/stores/media-query';

    // Components
    import HorizontalShelf from '~/components/jet/sections/HorizontalShelf.svelte';

    const { GRID_VALUES } = ShelfConfig.get();
    const { BREAKPOINTS } = ArtworkConfig.get();
    const mediaQueryStore = buildMediaQueryStore(
        'medium',
        getMediaConditions(BREAKPOINTS, { offset: 260 }),
    );

    export let shelf: Section<unknown>;
    export let itemComponent: typeof SvelteComponent;

    $: isListScreen = !!$listScreenStore.detailPage;

    $: itemKind = isListScreen ? 'shelfListItemListScreen' : shelf.itemKind;
    $: items = shelf.items;
    $: config = getShelfConfig(itemKind);
    $: gridType = config?.gridType;
    $: gridRows = config?.gridRows;

    $: gridColumns = GRID_VALUES?.[gridType]?.[$mediaQueryStore];
    $: gridVisibleItems = gridColumns * gridRows;
    // Determine if shelf includes Recordings Page Footer Lockup
    $: isFromAlbum = (items[0] as any)?.isFromAlbum;
    $: useDenseColumns =
        $mediaQueryStore !== 'xsmall' &&
        items.length < gridVisibleItems &&
        !isFromAlbum;
    $: useSingleColumn = $sidebarIsHidden || isFromAlbum;

    $: hideKeylineBorder = (index: number) => {
        if (useSingleColumn) {
            return index === 0;
        } else if (useDenseColumns) {
            return index < gridColumns;
        } else {
            return index % gridRows === 0;
        }
    };
</script>

<div
    data-testid="shelf-list-container"
    class="shelf-list"
    class:shelf-list--single-column={useSingleColumn}
    class:shelf-list--grid-view={useDenseColumns}
>
    <HorizontalShelf {items} {gridType} {gridRows} let:item let:index>
        <svelte:component
            this={itemComponent}
            {item}
            --shelf-list-item-keyline-border={hideKeylineBorder(index)
                ? 'none'
                : null}
        />
    </HorizontalShelf>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .shelf-list {
        padding-bottom: $global-shelf-spacer-bottom;
    }

    .shelf-list--single-column {
        :global(.shelf-grid__list) {
            grid-auto-columns: unset;
            grid-auto-flow: row;
        }

        // In single column view, display 5 items max on shelf
        :global(.shelf-grid__list-item:nth-child(n + 6)) {
            display: none;
        }
    }

    .shelf-list--grid-view {
        :global(.shelf-grid__list) {
            grid-auto-flow: dense;

            @media (--small) {
                grid-template-columns: repeat(var(--grid-small), 1fr);
            }

            @media (--medium) {
                grid-template-columns: repeat(var(--grid-medium), 1fr);
            }

            @media (--large) {
                grid-template-columns: repeat(var(--grid-large), 1fr);
            }
        }
    }
</style>
